import { Calendar, Plus, Wrench, Clock, Edit, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'

export interface Maintenance {
  id: number
  titre: string
  description: string | null
  date: string
  temps_heures?: number | null
  created_at: string
}

interface MaintenanceSectionProps {
  maintenances: Maintenance[]
  onNewMaintenance: () => void
  onEditMaintenance?: (maintenance: Maintenance) => void
  onDeleteMaintenance?: (maintenanceId: number, maintenanceTitle: string) => void
  sortOrder: 'oldest' | 'newest'
  onSortOrderChange: (order: 'oldest' | 'newest') => void
  loading?: boolean
  isAdmin?: boolean
}

export function MaintenanceSection({ maintenances, onNewMaintenance, onEditMaintenance, onDeleteMaintenance, sortOrder, onSortOrderChange, loading = false, isAdmin = true }: MaintenanceSectionProps) {
  // Format date to "DD month YYYY" (e.g., "20 janvier 2025")
  const formatDateLong = (dateString: string): string => {
    const date = new Date(dateString)
    const day = date.getDate()
    const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  // Sort maintenances by date based on sortOrder
  const sortedMaintenances = [...maintenances].sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA
  })

  return (
    <div className="bg-white rounded-[18px] border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#1CB5C81A' }}
          >
            <Wrench className="w-5 h-5 text-cyan-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Suivi des Maintenances</h2>
            <p className="text-sm text-gray-500">Historique des interventions de maintenance effectuées sur le projet</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tri :</span>
            <select
              value={sortOrder}
              onChange={(e) => onSortOrderChange(e.target.value as 'oldest' | 'newest')}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-cyan-400 focus:border-cyan-400"
            >
              <option value="oldest">Plus anciennes</option>
              <option value="newest">Plus récentes</option>
            </select>
          </div>
          {isAdmin && (
            <Button 
              onClick={onNewMaintenance}
              className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <PageSkeleton variant="rows" />
      ) : sortedMaintenances.length === 0 ? (
        <div className="text-center py-8 text-gray-500">Aucune maintenance enregistrée</div>
      ) : (
        <div className="space-y-4">
          {sortedMaintenances.map((maintenance) => (
            <div 
              key={maintenance.id} 
              className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 relative"
            >
              {/* Left border line */}
              <div 
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
                style={{ backgroundColor: '#1CB5C8' }}
              />
              
              <div className="ml-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800">{maintenance.titre}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 text-sm" style={{ color: '#6A7282' }}>
                      <Calendar className="w-4 h-4" />
                      <span>{formatDateLong(maintenance.date)}</span>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        {onEditMaintenance && (
                          <button
                            onClick={() => onEditMaintenance(maintenance)}
                            className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                            title="Modifier la maintenance"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {onDeleteMaintenance && (
                          <button
                            onClick={() => onDeleteMaintenance(maintenance.id, maintenance.titre)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer la maintenance"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {maintenance.description && (
                  <p className="text-sm text-gray-600 mb-3 break-words whitespace-normal">{maintenance.description}</p>
                )}
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm" style={{ color: '#6A7282' }}>
                  {maintenance.temps_heures && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{maintenance.temps_heures}h</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Ajouté le {formatDateLong(maintenance.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

